/**
 * Readable shades of a theme-builder colour (owner, 2026-09-27; spec
 * docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md, "Ruling 5").
 *
 * The builder lets a person pick any colour — nine in a grid, or their own. A light pick (yellow,
 * green, sky) filled buttons well but was unreadable wherever the app wrote it as text: 1.53:1 for
 * #facc15 on the white page. The rule:
 *
 *   - the chosen colour still FILLS buttons, badges and accents exactly as picked — nothing here
 *     computes a fill;
 *   - TEXT (links, figures, pressed or selected labels) is the colour mixed toward black in light mode,
 *     toward white in dark, until it reads at 4.5:1 on the page background and on cards — and on its
 *     own tint over them up to 20% (TINT_BUDGET: a chip on a highlighted card) — and no further;
 *   - the FOCUS RING is mixed the same way to 3:1;
 *   - the INK on a filled primary is white or near-black, whichever reads better.
 *
 * Mixing is in OKLab, the space CSS `color-mix(in oklab, …)` uses: lightness and chroma move toward
 * the end together, so the hue stays. Surfaces are Classic's (app/globals.css :root and .dark) — the
 * builder's look is Classic's tokens with the builder on top (Custom, or no theme chosen); a ready-made
 * theme brings its own colours. Pure functions, no DOM.
 */
import {
  composite,
  contrastRatio,
  oklchToRgb,
  parseCssColour,
  rgbToHex,
  rgbToOklab,
  worstContrast,
} from './app-themes/colour-space'

export type BuilderMode = 'light' | 'dark'

/** Classic's page background and card, per mode (app/globals.css; a spec pins them). */
export const BUILDER_SURFACES: Record<BuilderMode, readonly [background: string, card: string]> = {
  light: ['oklch(1 0 0)', 'oklch(1 0 0)'],
  dark: ['oklch(0.145 0 0)', 'oklch(0.205 0 0)'],
}

/**
 * The two inks for a filled primary. The near-black is a step below the app's text black
 * (oklch 0.145): at that black the grid's violet reads 4.497:1, just under the bar.
 */
export const BUILDER_INKS = { white: '#ffffff', nearBlack: 'oklch(0.1 0 0)' } as const

export const TEXT_CONTRAST = 4.5
export const RING_CONTRAST = 3
/** How far each step mixes toward the end: 0.5%. */
export const MIX_STEP = 0.005
/**
 * The strongest tint of the colour that text in the colour sits on: the app draws selected chips at
 * 10% (bg-primary/10), highlights cards at 5%, and some fills at 20%. The headed pass read a step
 * number at 4.43:1 on a 10% chip inside a 5% card, so text is held at 4.5:1 up to 20%.
 */
export const TINT_BUDGET = 0.2

export interface BuilderShades {
  /** Primary written as text. */
  text: string
  /** The focus ring. */
  ring: string
  /** Words on a filled primary. */
  ink: string
}

/** `colour` mixed toward black or white by `t` (0..1) in OKLab, as #rrggbb. */
export function mixToward(colour: string, toward: 'black' | 'white', t: number): string {
  const [l, a, b] = rgbToOklab(parseCssColour(colour).rgb)
  const k = Math.min(1, Math.max(0, t))
  const L = toward === 'black' ? l * (1 - k) : l + (1 - l) * k
  const [A, B] = [a * (1 - k), b * (1 - k)]
  const chroma = Math.hypot(A, B)
  const hue = ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360
  return rgbToHex(oklchToRgb(L, chroma, hue).rgb)
}

/**
 * The least mix of `colour` toward black or white that reads at `need` on every surface — and, given
 * `tintOf`, also over that colour's tint on each surface at TINT_BUDGET (a chip on a highlighted card).
 * `t` is how far it mixed; 0 means the colour already reads as picked.
 */
export function readableShade(
  colour: string,
  surfaces: readonly string[],
  need: number,
  toward: 'black' | 'white',
  tintOf?: string,
): { hex: string; t: number } {
  const grounds = surfaces.map((s) => parseCssColour(s).rgb)
  const tint = tintOf === undefined ? null : parseCssColour(tintOf).rgb
  const steps = Math.round(1 / MIX_STEP)
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const hex = mixToward(colour, toward, t)
    const rgb = parseCssColour(hex).rgb
    const reads = surfaces.every((s, j) => {
      if (worstContrast(hex, s) < need) return false
      return tint === null || contrastRatio(rgb, composite(tint, TINT_BUDGET, grounds[j])) >= need
    })
    if (reads) return { hex, t }
  }
  return { hex: toward === 'black' ? '#000000' : '#ffffff', t: 1 }
}

/** White or near-black, whichever reads better on `colour` (white on a tie). */
export function inkOn(colour: string): string {
  const white = worstContrast(BUILDER_INKS.white, colour)
  const nearBlack = worstContrast(BUILDER_INKS.nearBlack, colour)
  return white >= nearBlack ? BUILDER_INKS.white : BUILDER_INKS.nearBlack
}

const cache = new Map<string, BuilderShades>()

/** The text, ring and ink for a builder colour in a mode. Throws on a colour it cannot read. */
export function builderShades(colour: string, mode: BuilderMode): BuilderShades {
  const key = `${colour.trim().toLowerCase()}|${mode}`
  const hit = cache.get(key)
  if (hit) return hit
  const surfaces = BUILDER_SURFACES[mode]
  const toward = mode === 'light' ? 'black' : 'white'
  const shades: BuilderShades = {
    text: readableShade(colour, surfaces, TEXT_CONTRAST, toward, colour).hex,
    ring: readableShade(colour, surfaces, RING_CONTRAST, toward).hex,
    ink: inkOn(colour),
  }
  cache.set(key, shades)
  return shades
}
